import { ShoppingBag, Plus, Minus, Trash2, ExternalLink } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useShopifyCart } from '@/hooks/useShopifyCart';
import { useFloatingActions } from '@/contexts/FloatingActionsContext';
import { Link, useLocation } from 'react-router-dom';
// getShopifyCartItemUrl removed - use direct Shopify URLs instead
import TrustIndicators from './TrustIndicators';


interface ShopifyCartSidebarProps {
  children: React.ReactNode;
  showBadgeOnScroll?: boolean; // New prop to control badge visibility based on scroll
}

export const ShopifyCartSidebar = ({ children, showBadgeOnScroll = false }: ShopifyCartSidebarProps) => {
  const location = useLocation();
  const { showFloatingActions, setCartOpen } = useFloatingActions();
  const { 
    items, 
    updateQuantity, 
    removeFromCart, 
    clearCart, 
    getTotalItems, 
    getTotalPrice,
    getTotalSavings,
    getCheckoutUrl 
  } = useShopifyCart();

  const formatPrice = (amount: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(parseFloat(amount));
  };

  const totalItems = getTotalItems();
  const totalPrice = getTotalPrice();
  const totalSavings = getTotalSavings();
  
  // Determine if badge should show based on scroll behavior or always
  const shouldShowBadge = showBadgeOnScroll ? (totalItems > 0 && showFloatingActions) : totalItems > 0;

  return (
    <Sheet onOpenChange={(open) => setCartOpen(open)}>
      <SheetTrigger asChild>
        <div className="relative cursor-pointer">
          {children}
          {shouldShowBadge && (
            <Badge 
              variant="destructive" 
              className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center p-0 text-xs"
            >
              {totalItems}
            </Badge>
          )}
        </div>
      </SheetTrigger>
      
      <SheetContent className="w-[90vw] max-w-[420px] sm:max-w-[580px] flex flex-col h-full">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5" aria-hidden="true" />
              Shopping Cart ({totalItems} {totalItems === 1 ? 'item' : 'items'})
            </div>
            {items.length > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        clearCart();
                      }}
                      className="h-6 w-6 text-muted-foreground hover:text-destructive"
                      aria-label="Clear all items from cart"
                    >
                      <Trash2 className="w-3 h-3" aria-hidden="true" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="center">
                    <p>Clear Cart</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center space-y-4">
            <ShoppingBag className="w-16 h-16 text-muted-foreground" aria-hidden="true" />
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold">Your cart is empty</h3>
              <p className="text-muted-foreground">
                Discover our beautiful boho collection and add some items to your cart.
              </p>
            </div>
            <SheetClose asChild>
              <Link to="/products">
                <Button variant="default">
                  Browse Products
                </Button>
              </Link>
            </SheetClose>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto space-y-4 py-4 min-h-0 min-h-[260px]">
              {items.map((item) => (
                <div key={item.variant.id} className="flex gap-3 p-3 border rounded-lg">
                  <div className="w-16 h-16 bg-muted rounded-md overflow-hidden flex-shrink-0">
                    {item.product.images[0] ? (
                      <img
                        src={item.product.images[0].url}
                        alt={item.product.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingBag className="w-6 h-6 text-muted-foreground" aria-hidden="true" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="w-full">
                      <Link 
                        to={`/shopify-product/${item.product.handle}`}
                        className="block hover:text-primary transition-colors"
                      >
                        <h4 className="text-sm font-medium line-clamp-2 break-words hover:underline">
                          {item.product.title}
                        </h4>
                      </Link>
                      {item.variant.title !== 'Default Title' && (
                        <p className="text-xs text-muted-foreground truncate">{item.variant.title}</p>
                      )}
                    </div>

                    <div className="flex items-center justify-between gap-2 min-w-0">
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateQuantity(item.variant.id, item.quantity - 1)}
                          className="w-7 h-7 p-0"
                          aria-label={`Decrease quantity of ${item.product.title}`}
                        >
                          <Minus className="w-3 h-3" aria-hidden="true" />
                        </Button>
                        <span className="w-6 text-center text-sm font-medium" aria-label={`Quantity: ${item.quantity}`}>{item.quantity}</span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateQuantity(item.variant.id, item.quantity + 1)}
                          className="w-7 h-7 p-0"
                          disabled={item.quantity >= item.variant.quantityAvailable}
                          aria-label={`Increase quantity of ${item.product.title}`}
                        >
                          <Plus className="w-3 h-3" aria-hidden="true" />
                        </Button>
                      </div>

                      <div className="flex items-center gap-1 flex-shrink-0">
                        <div className="text-right">
                          {/* Show compareAt price (Shopify original price) or originalPrice */}
                          {(item.variant.compareAtPrice?.amount && 
                            parseFloat(item.variant.compareAtPrice.amount) > parseFloat(item.variant.price.amount)) && (
                            <div className="text-xs text-muted-foreground line-through">
                              {formatPrice(item.variant.compareAtPrice.amount)}
                            </div>
                          )}
                          {/* Show individual item savings */}
                          {(item.variant.compareAtPrice?.amount && 
                            parseFloat(item.variant.compareAtPrice.amount) > parseFloat(item.variant.price.amount)) && (
                            <div className="text-xs text-primary font-medium">
                              Save {formatPrice((parseFloat(item.variant.compareAtPrice.amount) - parseFloat(item.variant.price.amount)).toString())}
                            </div>
                          )}
                          <span className="text-sm font-semibold truncate max-w-[80px]">
                            {formatPrice(item.variant.price.amount)}
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeFromCart(item.variant.id)}
                          className="w-7 h-7 p-0 text-destructive hover:text-destructive"
                          aria-label={`Remove ${item.product.title} from cart`}
                        >
                          <Trash2 className="w-3 h-3" aria-hidden="true" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t pt-4 space-y-4 flex-shrink-0">
              {totalSavings > 0 && (
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-3">
                  <div className="flex justify-between items-center text-primary">
                    <span className="font-medium">💰 Total Savings:</span>
                    <span className="text-lg font-bold">-{formatPrice(totalSavings.toString())}</span>
                  </div>
                  <p className="text-xs text-primary mt-1">
                    You're saving on sale items!
                  </p>
                </div>
              )}
              
              <div className="flex justify-between text-lg font-semibold">
                <span>Total:</span>
                <span>{formatPrice(totalPrice.toString())}</span>
              </div>

              <div className="space-y-3 pt-2 border-t">
                <TrustIndicators variant="sidebar" />
                
                <Button 
                  asChild
                  className="w-full" 
                  size="lg"
                  disabled={items.length === 0}
                >
                  <a 
                    href={getCheckoutUrl()} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2"
                  >
                    Secure Checkout
                    <ExternalLink className="w-4 h-4" aria-hidden="true" />
                  </a>
                </Button>
              </div>


              <div className="text-center pt-4 border-t">
                <SheetClose asChild>
                  <Link to="/products">
                    <Button variant="ghost" className="text-sm">
                      Continue Shopping
                    </Button>
                  </Link>
                </SheetClose>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};